import { supabase } from "@/lib/supabase";
import {
  type LinkedTask,
  releaseBodyTask,
  resolveReleaseTasks,
  TASK_ORG,
  TASK_PROJECT,
  type TaskLinkRow,
} from "@/lib/releaseTaskLinks";
import type { ReleasePr } from "./releases";

// Authenticated RLS reads use the same canonical store as the task-link gate.
// This supplements older Edge deployments on a preview; no writes or new auth.
export async function enrichReleaseTasks(
  prs: ReleasePr[],
): Promise<ReleasePr[]> {
  if (!prs.length) return prs;
  try {
    const links: TaskLinkRow[] = [];
    for (const repo of new Set(prs.map((pr) => pr.repo))) {
      const numbers = prs.filter((pr) => pr.repo === repo).map((pr) =>
        pr.number
      );
      for (let start = 0; start < numbers.length; start += 100) {
        for (let offset = 0;; offset += 500) {
          if (offset >= 10000) throw Error("task_link_inventory_incomplete");
          const result = await supabase.from("task_pull_requests").select(
            "repo,number,task_id,head_sha,manual_override",
          )
            .eq("repo", repo).in("number", numbers.slice(start, start + 100))
            .order("number").range(offset, offset + 499);
          if (result.error) throw result.error;
          links.push(...result.data);
          if (result.data.length < 500) break;
        }
      }
    }
    const ids = [
      ...new Set([
        ...links.map((link) => link.task_id),
        ...prs.map((pr) => releaseBodyTask(pr.pr_body ?? "")),
      ].filter((id): id is string => Boolean(id))),
    ];
    const tasks: LinkedTask[] = [];
    for (let start = 0; start < ids.length; start += 100) {
      const result = await supabase.from("tasks").select(
        "id,title,description,status",
      ).in("id", ids.slice(start, start + 100))
        .eq("project_id", TASK_PROJECT).eq("organization_id", TASK_ORG).is(
          "archived_at",
          null,
        );
      if (result.error) throw result.error;
      tasks.push(...result.data);
    }
    return resolveReleaseTasks(prs, links, tasks);
  } catch {
    // An inaccessible source cannot establish that a PR has no link.
    return prs.map((pr) => ({ ...pr, task_link_state: "unknown" }));
  }
}
